import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";

import { useAuth } from "../context/AuthContext";
import { useApiQuery } from "../hooks/useApiQuery";
import { useDebounce } from "../hooks/useDebounce";

import {
  getSessions,
  getAllEvents,
  getMyEvents,
  getDevices,
} from "../services/api";

import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// ============================================================
// CONSTANTS
// ============================================================

const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 50;

// ============================================================
// MAIN COMPONENT
// ============================================================

const SessionLogs = () => {
  const { user } = useAuth();

  const tableRef = useRef(null);

  // ==========================================================
  // FILTER STATE
  // ==========================================================

  const [filters, setFilters] = useState({
    search: "",
    eventId: "",
    deviceId: "",
    status: "",
    sortOrder: "desc",
    startDate: "",
    endDate: "",
  });

  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounce(filters.search, DEBOUNCE_DELAY);

  // ==========================================================
  // SESSIONS
  // ==========================================================

  const {
    data: sessionsResponse,
    isLoading: sessionsLoading,
    isFetching: sessionsFetching,
    isError: sessionsError,
    refetch,
  } = useApiQuery(
    [
      "sessions",
      page,
      PAGE_SIZE,
      debouncedSearch,
      filters.eventId,
      filters.deviceId,
      filters.status,
      filters.sortOrder,
      filters.startDate,
      filters.endDate,
    ],

    () =>
      getSessions({
        page,
        limit: PAGE_SIZE,

        eventId: filters.eventId || undefined,

        deviceId: filters.deviceId || undefined,

        status: filters.status || undefined,

        startDate: filters.startDate || undefined,

        endDate: filters.endDate || undefined,

        search: debouncedSearch || undefined,

        sortOrder: filters.sortOrder,

        // IMPORTANT:
        // Your current backend does not yet
        // support search/sortOrder.
        //
        // We therefore do not send them yet.
      }),

    {
      enabled: true,

      staleTime: 30 * 1000,

      refetchOnWindowFocus: false,

      placeholderData: (previousData) => previousData,

      onError: (error) => {
        console.error("Failed to fetch sessions:", error);
      },
    },
  );

  // ==========================================================
  // RESPONSE DATA
  // ==========================================================

  const sessions = sessionsResponse?.data || [];

  const pagination = sessionsResponse?.pagination || {
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };

  // ==========================================================
  // EVENTS
  // ==========================================================

  const {
    data: eventsResponse,
    isLoading: eventsLoading,
    isError: eventsError,
  } = useApiQuery(
    ["all-events"],
    async () => {
      const res = await getAllEvents();

      // API can return:
      // 1. [events]
      // 2. { data: [events] }
      // 3. { events: [events] }

      return res.data;
    },
    {
      staleTime: 5 * 60 * 1000,

      onError: (error) => {
        console.error("Failed to load events:", error);
      },
    },
  );

  // Always make sure events is an array
  const events = useMemo(() => {
    if (Array.isArray(eventsResponse)) {
      return eventsResponse;
    }

    if (Array.isArray(eventsResponse?.data)) {
      return eventsResponse.data;
    }

    if (Array.isArray(eventsResponse?.events)) {
      return eventsResponse.events;
    }

    return [];
  }, [eventsResponse]);

  // ==========================================================
  // DEVICES
  // ==========================================================

  const { data: allDevices = [] } = useApiQuery(
    ["devices"],
    () => getDevices().then((res) => res.data || []),
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  // ==========================================================
  // EVENT DEVICES
  // ==========================================================

  const eventDevices = useMemo(() => {
    if (!filters.eventId) {
      return allDevices;
    }

    return allDevices.filter(
      (device) =>
        device.eventId?._id === filters.eventId ||
        device.eventId === filters.eventId,
    );
  }, [allDevices, filters.eventId]);

  // ==========================================================
  // STATS
  // ==========================================================
  //
  // IMPORTANT:
  // These are statistics for the CURRENT PAGE only.
  //
  // If you want total entries/exits across ALL matching
  // sessions, we'll create a separate backend analytics
  // endpoint later.
  //
  // ==========================================================

  const stats = useMemo(() => {
    const total = sessions.length;

    const entries = sessions.filter(
      (session) => session.status === "IN",
    ).length;

    const exits = sessions.filter((session) => session.status === "OUT").length;

    return {
      total,
      entries,
      exits,
    };
  }, [sessions]);

  // ==========================================================
  // FILTER HANDLER
  // ==========================================================

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));

    // Backend pagination starts at 1
    setPage(1);
  }, []);

  // ==========================================================
  // CLEAR FILTERS
  // ==========================================================

  const clearFilters = useCallback(() => {
    setFilters({
      search: "",
      eventId: "",
      deviceId: "",
      status: "",
      sortOrder: "desc",
      startDate: "",
      endDate: "",
    });

    setPage(1);
  }, []);

  // ==========================================================
  // REFRESH
  // ==========================================================

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const goToPage = useCallback((newPage) => {
      const safePage = Math.max(
        1,
        Math.min(newPage, pagination.totalPages || 1),
      );

      setPage(safePage);

      if (tableRef.current) {
        tableRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    },
    [pagination.totalPages],
  );

  // ==========================================================
  // RESET DEVICE WHEN EVENT CHANGES
  // ==========================================================

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      deviceId: "",
    }));

    setPage(1);
  }, [filters.eventId]);

  // ==========================================================
  // KEYBOARD SHORTCUT
  // ==========================================================

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "r") {
        event.preventDefault();

        handleRefresh();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleRefresh]);

  // ==========================================================
  // SESSION SCANNED EVENT
  // ==========================================================

  useEffect(() => {
    const handleScan = () => {
      refetch();
    };

    window.addEventListener("sessionScanned", handleScan);

    return () => {
      window.removeEventListener("sessionScanned", handleScan);
    };
  }, [refetch]);

  // ==========================================================
  // ACTIVE FILTERS
  // ==========================================================

  const hasActiveFilters = Boolean(
    filters.search ||
    filters.eventId ||
    filters.deviceId ||
    filters.status ||
    filters.startDate ||
    filters.endDate,
  );

  // ==========================================================
  // LOADING
  // ==========================================================

  if (sessionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="
              inline-block
              w-10
              h-10
              border-2
              border-[#1672b9]
              border-t-transparent
              rounded-full
              animate-spin
              mb-3
            "
          />

          <p className="text-gray-500 text-sm">Loading session logs...</p>
        </div>
      </div>
    );
  }

  // ==========================================================
  // ERROR
  // ==========================================================

  if (sessionsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-500 text-lg">Failed to load session logs</p>

          <button
            onClick={handleRefresh}
            className="
              mt-3
              px-4
              py-2
              bg-[#1672b9]
              text-white
              rounded-lg
              text-sm
            "
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div className="space-y-6">
      {/* ====================================================
          HEADER
      ==================================================== */}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Session Logs</h1>

          <p className="text-sm text-gray-500">
            View and filter all scan sessions
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            <kbd className="px-2 py-1 bg-gray-100 rounded text-xs">Ctrl+R</kbd>{" "}
            to refresh
          </span>

          <button
            onClick={handleRefresh}
            disabled={sessionsFetching}
            className="
              p-2
              text-gray-400
              hover:text-[#1672b9]
              transition-colors
              rounded-lg
              hover:bg-gray-50
              disabled:opacity-50
            "
            title="Refresh (Ctrl+R)"
          >
            <ArrowPathIcon
              className={`
                w-5 h-5
                ${sessionsFetching ? "animate-spin" : ""}
              `}
            />
          </button>
        </div>
      </div>

      {/* ====================================================
          STATS
      ==================================================== */}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            label: "Sessions on Page",
            value: stats.total,
            color: "#1672b9",
          },
          {
            label: "Entries on Page",
            value: stats.entries,
            color: "#22c55e",
          },
          {
            label: "Exits on Page",
            value: stats.exits,
            color: "#64748b",
          },
        ].map((stat, index) => (
          <div
            key={index}
            className="
                bg-white
                rounded-xl
                border
                border-gray-100
                shadow-sm
                p-4
              "
          >
            <div className="flex items-center gap-3">
              <div
                className="p-2 rounded-lg"
                style={{
                  backgroundColor: `${stat.color}10`,
                }}
              >
                <ClockIcon
                  className="w-5 h-5"
                  style={{
                    color: stat.color,
                  }}
                />
              </div>

              <div>
                <p className="text-sm text-gray-500">{stat.label}</p>

                <p
                  className="text-2xl font-bold"
                  style={{
                    color: stat.color,
                  }}
                >
                  {stat.value}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ====================================================
          FILTERS
      ==================================================== */}

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="w-5 h-5 text-[#1672b9]" />

            <h2 className="text-base font-medium text-gray-900">Filters</h2>

            {hasActiveFilters && (
              <span className="px-2 py-0.5 text-xs bg-[#1672b9]/10 text-[#1672b9] rounded-full">
                Active
              </span>
            )}
          </div>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="
                flex
                items-center
                gap-1
                text-sm
                text-gray-500
                hover:text-red-500
                transition-colors
              "
            >
              <XMarkIcon className="w-4 h-4" />
              Clear all
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}

          <div className="relative">
            <MagnifyingGlassIcon
              className="
                absolute
                left-3
                top-1/2
                -translate-y-1/2
                w-4
                h-4
                text-gray-400
              "
            />

            <input
              type="text"
              placeholder="Search by ID or Name"
              value={filters.search}
              onChange={(event) => updateFilter("search", event.target.value)}
              className="
                w-full
                pl-9
                pr-3
                py-2
                border
                border-gray-200
                rounded-lg
                text-sm
                focus:outline-none
                focus:ring-2
                focus:ring-[#1672b9]/20
                focus:border-[#1672b9]
              "
            />
          </div>

          {/* Event */}

          <select
            value={filters.eventId}
            onChange={(event) => updateFilter("eventId", event.target.value)}
            className="
              w-full
              border
              border-gray-200
              rounded-lg
              px-3
              py-2
              text-sm
            "
          >
            <option value="">All Events</option>

            {events.map((event) => (
              <option key={event._id} value={event._id}>
                {event.name}
              </option>
            ))}
          </select>

          {/* Device */}

          <select
            value={filters.deviceId}
            onChange={(event) => updateFilter("deviceId", event.target.value)}
            className="
              w-full
              border
              border-gray-200
              rounded-lg
              px-3
              py-2
              text-sm
            "
          >
            <option value="">
              {filters.eventId ? "All Devices in Event" : "All Devices"}
            </option>

            {eventDevices.map((device) => (
              <option key={device._id} value={device._id}>
                {device.name}
              </option>
            ))}
          </select>

          {/* Status */}

          <select
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
            className="
              w-full
              border
              border-gray-200
              rounded-lg
              px-3
              py-2
              text-sm
            "
          >
            <option value="">All Status</option>

            <option value="IN">Entry</option>

            <option value="OUT">Exit</option>
          </select>

          {/* Start Date */}

          <input
            type="date"
            value={filters.startDate}
            onChange={(event) => updateFilter("startDate", event.target.value)}
            className="
              w-full
              border
              border-gray-200
              rounded-lg
              px-3
              py-2
              text-sm
            "
          />

          {/* End Date */}

          <input
            type="date"
            value={filters.endDate}
            onChange={(event) => updateFilter("endDate", event.target.value)}
            className="
              w-full
              border
              border-gray-200
              rounded-lg
              px-3
              py-2
              text-sm
            "
          />
        </div>

        <div className="mt-4 text-sm text-gray-500">
          {pagination.total || 0} sessions found
          {sessionsFetching && (
            <span className="ml-2 text-xs text-[#1672b9]">Updating...</span>
          )}
        </div>
      </div>

      {/* ====================================================
          TABLE
      ==================================================== */}

      <div
        ref={tableRef}
        className="
          bg-white
          rounded-xl
          border
          border-gray-100
          shadow-sm
          overflow-hidden
        "
      >
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50/50">
              <tr>
                {[
                  "User ID",
                  "Name",
                  "Email",
                  "Event",
                  "Device",
                  "Status",
                  "Time",
                  "Date",
                ].map((header) => (
                  <th
                    key={header}
                    className="
                      px-6
                      py-3
                      text-left
                      text-xs
                      font-medium
                      text-gray-500
                      uppercase
                      tracking-wider
                    "
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <p className="text-gray-500">No sessions found</p>

                    <p className="text-xs text-gray-400 mt-1">
                      {hasActiveFilters
                        ? "Try adjusting your filters"
                        : "Sessions will appear here when attendees check in"}
                    </p>
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr
                    key={session._id}
                    className="
                      hover:bg-gray-50/50
                      transition-colors
                    "
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                      {session.customId || session.userId?.customId || "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {session.name || session.userId?.name || "Unknown"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.email || session.userId?.email || "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {session.eventId?.name || "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {session.entryDevice?.name || "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`
                          px-2.5
                          py-0.5
                          rounded-full
                          text-xs
                          font-medium
                          ${
                            session.status === "IN"
                              ? "bg-[#1672b9]/10 text-[#1672b9]"
                              : "bg-gray-100 text-gray-500"
                          }
                        `}
                      >
                        {session.status === "IN" ? "ENTRY" : "EXIT"}
                      </span>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.entryTime
                        ? new Date(session.entryTime).toLocaleTimeString()
                        : "—"}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {session.entryTime
                        ? new Date(session.entryTime).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ==================================================
            PAGINATION
        ================================================== */}

        {pagination.totalPages > 1 && (
          <div
            className="
            px-6
            py-4
            border-t
            border-gray-100
            flex
            items-center
            justify-between
          "
          >
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </span>

            <div className="flex gap-2">
              <button
                onClick={() => goToPage(page - 1)}
                disabled={!pagination.hasPreviousPage || sessionsFetching}
                className="
                  px-3
                  py-1
                  border
                  border-gray-200
                  rounded
                  text-sm
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                  hover:bg-gray-50
                "
              >
                Previous
              </button>

              <button
                onClick={() => goToPage(page + 1)}
                disabled={!pagination.hasNextPage || sessionsFetching}
                className="
                  px-3
                  py-1
                  border
                  border-gray-200
                  rounded
                  text-sm
                  disabled:opacity-50
                  disabled:cursor-not-allowed
                  hover:bg-gray-50
                "
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SessionLogs;
